import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import PinDropIcon from "@mui/icons-material/PinDrop";
import SecurityIcon from "@mui/icons-material/Security";
import { Box, CircularProgress, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { colors, alphaVar } from "../../../ui/colors";

const useInternalLoaderDelay = (isPending, isSuccess, delayMs = 2500) => {
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    if (isPending) {
      setShowLoader(true);
    } else if (!isPending && !isSuccess) {
      setShowLoader(false);
    }
  }, [isPending, isSuccess, delayMs]);

  return showLoader;
};

export const GenericProcessLoader = ({ isPending, isSuccess, delayMs = 3000, onFinish, status = {} }) => {
  const showLoader = useInternalLoaderDelay(isPending, isSuccess, delayMs);
  const [showSuccess, setShowSuccess] = useState(false);
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  useEffect(() => {
    if (showLoader) {
      const timerSuccess = setTimeout(() => setShowSuccess(true), 4000);
      const timerFinish = setTimeout(() => {
        if (typeof onFinishRef.current === "function") {
          onFinishRef.current();
        }
      }, 7000);

      return () => {
        clearTimeout(timerSuccess);
        clearTimeout(timerFinish);
      };
    }
  }, [showLoader]);

  if (!showLoader) return null;

  // Dynamically build steps based on provided status keys
  const steps = [];

  if (status.first) {
    steps.push({
      text: `${status.first}...`,
      icon: <PersonAddAltIcon sx={{ fontSize: 34, color: "#fff" }} />,
      gradient: `linear-gradient(135deg, ${colors.orange[400]}, ${colors.orange[600]})`,
    });
  }

  if (status.second) {
    steps.push({
      text: `${status.second}...`,
      icon: <PinDropIcon sx={{ fontSize: 34, color: "#fff" }} />,
      gradient: `linear-gradient(135deg, ${colors.yellow[400]}, ${colors.orange[500]})`,
    });
  }

  if (status.third) {
    steps.push({
      text: `${status.third}...`,
      icon: <SecurityIcon sx={{ fontSize: 34, color: "#fff" }} />,
      gradient: `linear-gradient(135deg, ${colors.success[400]}, ${colors.success[600]})`,
    });
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: 260,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 4,
          textAlign: "center",
        }}
      >
        {steps.map((step, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: i * 0.5, duration: 0.8 }}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              minWidth: 150,
            }}
          >
            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: "50%",
                background: step.gradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 6px 16px ${alphaVar(colors.gray[600], 0.25)}`,
              }}
            >
              {step.icon}
            </Box>

            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: colors.gray[700],
                letterSpacing: 0.4,
              }}
            >
              {step.text}
            </Typography>
          </motion.div>
        ))}

        <motion.div initial={{ opacity: 0, rotate: -90 }} animate={{ opacity: 1, rotate: 0 }} transition={{ delay: 1.5, duration: 1 }}>
          <CircularProgress size={36} thickness={4} sx={{ color: colors.orange.base, ml: 1 }} />
        </motion.div>
      </Box>

      {showSuccess && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            backgroundColor: colors.success[300] + "CC",
            animation: "fadeInOut 3s ease forwards",
            "@keyframes fadeInOut": {
              "0%": { opacity: 0 },
              "10%": { opacity: 1 },
              "80%": { opacity: 1 },
              "100%": { opacity: 0 },
            },
          }}
        >
          <CheckCircleOutline
            sx={{
              fontSize: 120,
              color: "#fff",
              animation: "popScale 1s ease forwards",
              "@keyframes popScale": {
                "0%": { transform: "scale(0)", opacity: 0 },
                "40%": { transform: "scale(1.3)", opacity: 1 },
                "70%": { transform: "scale(0.95)", opacity: 1 },
                "100%": { transform: "scale(1)", opacity: 1 },
              },
            }}
          />

          <Typography
            variant="h5"
            sx={{
              color: "#fff",
              fontWeight: 700,
              mt: 2,
              opacity: 0,
              animation: "fadeText 1.2s ease 0.6s forwards",
              "@keyframes fadeText": {
                from: { opacity: 0, transform: "translateY(10px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            Process Complete!
          </Typography>
        </Box>
      )}
    </Box>
  );
};
